import { z } from 'zod'

/**
 * PD-1600 — the language a scribed encounter is conducted in. One value per
 * session: Zoom live transcription pins one speaking language for every
 * participant, and the note is always written in English. Adding a language is
 * a one-line change here; the zod enum, the picker, the label and the
 * disclosure sentence all follow. Browser-safe: shared with both portals
 * through `@behuman/core-contract`.
 */
export const SCRIBE_SOURCE_LANGUAGES = ['en', 'es'] as const
export const scribeSourceLanguageSchema = z.enum(SCRIBE_SOURCE_LANGUAGES)
export type ScribeSourceLanguage = z.infer<typeof scribeSourceLanguageSchema>

export const SCRIBE_LANGUAGE_LABELS: Record<ScribeSourceLanguage, string> = {
  en: 'English',
  es: 'Spanish',
}

export function isScribeSourceLanguage(value: unknown): value is ScribeSourceLanguage {
  return typeof value === 'string' && (SCRIBE_SOURCE_LANGUAGES as readonly string[]).includes(value)
}

/**
 * The sentence that opens the AI Documentation Disclosure of a note written
 * from a non-English encounter (PD-1600 requirement 13). `undefined` for an
 * English encounter, so every English note reads exactly as before.
 */
export function encounterLanguageDisclosure(language: string | undefined): string | undefined {
  if (!language || language === 'en' || !isScribeSourceLanguage(language)) return undefined
  return `Encounter conducted in ${SCRIBE_LANGUAGE_LABELS[language]}; progress note generated in English with AI assistance.`
}
